// Native self-update for the privately-distributed Android app.
//
// WHY A SELF-UPDATE AT ALL
// The JS bundle ships INSIDE the app binary (capacitor.config.json's webDir;
// there is no live-update/CodePush plugin), so on native a WebView reload just
// re-shows the same stale local assets. That means EVERY new build — even a
// pure web change — needs a new APK. RoofBeam is distributed privately, not
// through a store, so before this the "Update available" banner could only
// tell the owner to go and update it himself.
//
// ⚠️ THE NATIVE HALF OF THIS IS ALREADY GONE FROM THE PLAY BUILD.
// Play's Device and Network Abuse policy forbids an app distributed through
// Play from updating itself by any route other than Play. As of the flavor
// split in android/app/build.gradle, AppInstaller.java lives in
// src/sideload/java/ and is physically absent from the `play` variant, which
// also drops REQUEST_INSTALL_PACKAGES (src/play/AndroidManifest.xml).
//
// NOTHING HERE NEEDS A FLAG: hasInstallerPlugin() asks Capacitor whether the
// plugin is actually compiled in, so on a play build checkForUpdate() already
// returns 'manual' instead of 'apk'.
//
// ⚠️ WHAT IS STILL WRONG ON A PLAY BUILD: 'manual' means "a newer APK exists,
// install it by hand", which is the wrong instruction in a store-distributed
// app — and latestAndroid is the SIDELOAD channel's version, which the Play
// build should not be comparing itself against at all. The Play answer is
// Play's In-App Updates API (com.google.android.play:app-update):
// AppUpdateManager's availableVersionCode replaces getLatestAndroid() and
// Play performs the install. Not built yet — the Play track is not live.
// scripts/androidRelease.js refuses to publish a play-variant APK to
// latestAndroid in the meantime, so the two channels cannot cross.
//
// WHAT IT DOES NOT DO
// It cannot install anything on its own. It downloads an APK from our own
// Firebase Hosting and opens Android's package installer; the owner taps
// Install in the system UI, and on Android 8+ has to grant "Allow from this
// source" once in system settings first (Android gives no in-app way to grant
// that). Everything here is a no-op off-native.
import { Capacitor, registerPlugin } from '@capacitor/core';
import { doc, getDocFromServer } from 'firebase/firestore';
import { db } from '../firebase/config';

const AppInstaller = registerPlugin('AppInstaller');

export function isNativeAndroid() {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
  } catch {
    return false;
  }
}

// Is the PUSH plugin compiled into the APK that is actually running? An older
// installed build predates @capacitor/push-notifications entirely, so calling
// it there throws "not implemented" rather than doing anything. The
// Notifications settings use this to say "install the latest app" instead of
// offering a toggle that silently does nothing.
export function hasPushPlugin() {
  try {
    if (!Capacitor.isNativePlatform()) return false;
    return Capacitor.isPluginAvailable('PushNotifications');
  } catch {
    return false;
  }
}

// Is the self-INSTALLER compiled in? Same problem as hasPushPlugin: a build
// older than this feature has no AppInstaller, so it can only be told to
// update by hand.
export function hasInstallerPlugin() {
  try {
    if (!isNativeAndroid()) return false;
    return Capacitor.isPluginAvailable('AppInstaller');
  } catch {
    return false;
  }
}

// The versionCode of the RUNNING app. @capacitor/app's getInfo().build is
// android:versionCode as a string. Null on web or if the call fails.
export async function getInstalledVersionCode() {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const { App } = await import('@capacitor/app');
    const info = await App.getInfo();
    const n = parseInt(info.build, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

// settings/company.latestAndroid — { versionCode, apkUrl, sha, publishedAt },
// written by scripts/publishAndroidRelease.js as part of the deploy. Read from
// the SERVER (not cache) so a just-shipped build is seen immediately.
// settings/company is already publicly readable (firestore.rules), so no rules
// change was needed for this.
export async function getLatestAndroid() {
  try {
    const snap = await getDocFromServer(doc(db, 'settings', 'company'));
    if (!snap.exists()) return null;
    const la = snap.data().latestAndroid;
    if (!la || !la.apkUrl || !la.versionCode) return null;
    return { versionCode: Number(la.versionCode), apkUrl: String(la.apkUrl), sha: la.sha || '', bytes: Number(la.bytes) || 0 };
  } catch {
    return null;
  }
}

// Decides what the "Update available" banner can actually offer.
//   kind: 'none'      nothing to do
//         'reload'    web — a reload genuinely fetches the new bundle
//         'apk'       native, a newer APK exists and this build can install it
//         'manual'    native, newer APK exists but this build has no installer
//                     plugin (or we can't read the installed versionCode)
export async function checkForUpdate() {
  if (!Capacitor.isNativePlatform()) return { kind: 'reload' };
  if (!isNativeAndroid()) return { kind: 'manual' };

  const [installed, latest] = [await getInstalledVersionCode(), await getLatestAndroid()];
  if (!latest) return { kind: 'manual', installed, latest: null };
  if (installed == null) return { kind: 'manual', installed: null, latest };
  if (latest.versionCode <= installed) return { kind: 'none', installed, latest };
  return { kind: hasInstallerPlugin() ? 'apk' : 'manual', installed, latest };
}

export async function canRequestInstall() {
  if (!hasInstallerPlugin()) return false;
  try {
    const res = await AppInstaller.canRequestInstall();
    return !!(res && res.allowed);
  } catch {
    return false;
  }
}

export async function openInstallSettings() {
  if (!hasInstallerPlugin()) return false;
  try { await AppInstaller.openInstallSettings(); return true; } catch { return false; }
}

// Downloads the APK into the app's own cache and opens the system installer.
// onStage is called with short status strings so the banner can show progress
// on what is a ~10 MB download over whatever connection the tablet has.
//
// Returns { ok } or { ok:false, error, needsPermission }.
export async function downloadAndInstall(onStage = () => {}) {
  if (!hasInstallerPlugin()) return { ok: false, error: 'This version of the app cannot install updates itself.' };

  const latest = await getLatestAndroid();
  if (!latest) return { ok: false, error: 'No published Android build to install.' };

  // Android 8+: without "Allow from this source" the installer opens and
  // immediately refuses, which looks like a broken button. Check first and
  // send the user to the right settings screen instead.
  if (!(await canRequestInstall())) {
    onStage('Permission needed');
    return {
      ok: false,
      needsPermission: true,
      error: 'Android needs permission first: tap "Open settings", turn on "Allow from this source", then come back and tap Update again.',
    };
  }

  try {
    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    const relPath = `updates/roofbeam-${latest.versionCode}.apk`;

    // BUG FIX (owner, on build 6):
    //   Error downloading file:
    //   /data/user/0/io.roofbeam.app/cache/updates/roofbeam-6.apk:
    //   open failed: ENOENT (No such file or directory)
    // `recursive: true` on downloadFile does NOT create the parent directory
    // on Android — the native implementation opens the destination for writing
    // and fails outright when its folder is missing. `updates/` has to exist
    // first, so make it explicitly. mkdir throws when the directory is already
    // there, which is the normal case on every update after the first, so that
    // is swallowed.
    try {
      await Filesystem.mkdir({ path: 'updates', directory: Directory.Cache, recursive: true });
    } catch (mkErr) {
      const m = (mkErr && (mkErr.message || mkErr.errorMessage)) || '';
      if (!/exist/i.test(m)) console.warn('updates mkdir:', m);  // already there = fine
    }

    // Clear previous downloads — each APK is ~8 MB and the cache would
    // otherwise accumulate one per update.
    try {
      const listing = await Filesystem.readdir({ path: 'updates', directory: Directory.Cache });
      for (const f of (listing.files || [])) {
        const name = typeof f === 'string' ? f : f.name;
        if (name && name.endsWith('.apk')) {
          await Filesystem.deleteFile({ path: `updates/${name}`, directory: Directory.Cache }).catch(() => {});
        }
      }
    } catch { /* empty or unreadable — nothing to clear */ }

    onStage('Downloading…');
    const res = await Filesystem.downloadFile({
      url: latest.apkUrl,
      path: relPath,
      directory: Directory.Cache,
      recursive: true,
    });

    // downloadFile returns the on-device path; fall back to getUri if a
    // platform build omits it.
    let path = res && res.path;
    if (!path) {
      const uri = await Filesystem.getUri({ path: relPath, directory: Directory.Cache });
      path = uri.uri;
    }

    // Verify we actually downloaded the APK and not something else. This is a
    // real hazard, not a theoretical one: firebase.json rewrites `**` to
    // /index.html, so a stale or wrong apkUrl returns 200 with the SPA shell —
    // a few KB of HTML that the installer would reject with a far more
    // confusing message than this one. latestAndroid.bytes is published
    // alongside the URL precisely so this check is possible.
    try {
      const st = await Filesystem.stat({ path: relPath, directory: Directory.Cache });
      const got = Number(st.size || 0);
      if (latest.bytes && got !== latest.bytes) {
        // ⚠ TWO DIFFERENT FAILURES, AND THEY ARE NOT THE USER'S FAULT IN
        // THE SAME WAY. This used to say "Check the connection" for both, and
        // that is what the owner saw on a Fold 8 for days while the real
        // problem was that the APK had been deleted from the server by an
        // unrelated hosting deploy. Telling somebody to check a connection
        // that is working is worse than saying nothing: they re-try, it
        // fails identically, and they stop trusting the updater.
        //
        // A missing file does NOT 404 here. The hosting rewrite answers 200
        // with the SPA shell, so what lands on disk is a couple of KB of
        // HTML. That is detectable and it is categorically a server problem.
        const tiny = got < 262144;                       // ~0.25 MB
        let looksLikeHtml = false;
        if (tiny) {
          // Only read when the file is already known to be small, so this
          // never pulls an 8 MB APK into memory to inspect it.
          try {
            const head = await Filesystem.readFile({ path: relPath, directory: Directory.Cache });
            const text = String((head && head.data) || '');
            // base64 of "<!DOCTYPE" / "<html" / "<" all begin "PCF" or "PGh" or "PA".
            looksLikeHtml = /^(PCF|PGh|PCE|PA)/.test(text) || /<!doctype|<html/i.test(text);
          } catch { /* cannot read it; the size alone still tells us enough */ }
        }
        if (tiny || looksLikeHtml) {
          return {
            ok: false,
            serverMissing: true,
            error: 'The update file is missing on the server — this isn\'t your connection. '
              + `Build ${latest.versionCode} was published but the installer is no longer at its address. `
              + 'Nothing on this tablet needs fixing; the update has to be re-published.',
          };
        }
        return {
          ok: false,
          error: `The download stopped partway (expected ${(latest.bytes / 1048576).toFixed(1)} MB, got ${(got / 1048576).toFixed(1)} MB). Check the connection and tap Try again.`,
        };
      }
    } catch { /* stat unavailable — carry on and let the installer judge */ }

    onStage('Opening installer…');
    await AppInstaller.install({ path });
    return { ok: true };
  } catch (e) {
    const raw = (e && (e.message || e.errorMessage)) || String(e);
    console.error('downloadAndInstall failed:', raw);
    // Plain language first, raw message kept on the end so a report is still
    // diagnosable. Every one of these leaves the panel's "Try again" button up.
    let friendly;
    if (/ENOENT|No such file/i.test(raw)) {
      friendly = "Couldn't save the download to the app's storage.";
    } else if (/network|timeout|unreachable|Failed to connect|ETIMEDOUT|ENOTFOUND/i.test(raw)) {
      friendly = "Couldn't reach the server — check the connection.";
    } else if (/space|ENOSPC/i.test(raw)) {
      friendly = 'Not enough free space on the tablet for the download (~8 MB).';
    } else if (/not implemented/i.test(raw)) {
      friendly = "This installed version can't install updates itself — install the latest app once by hand.";
    } else {
      friendly = 'The update could not be downloaded.';
    }
    return { ok: false, error: `${friendly} Tap Try again.\n\n(${raw})` };
  }
}
